import { useLoaderData } from 'react-router';
import { useAtom, useSetAtom } from 'jotai';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import { useSampleStock } from '@modules/product/hook/use-sample-stock';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';
import { ProductStatementText } from './ProductStatementText';
import { useProductLinkTracking } from '../hooks/use-product-link-tracking';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { cn } from '@rocco/utils/cn';
import {
    CONTENT_CAP_PADDED,
    DetailSummaryRow,
    DetailSaveButton,
    TrackedWebsiteCta,
    detailCta,
} from '~/ui-rocco/pages/detail-shared';
import { purchaseModalOpenAtom } from '../state/purchase-modal-atom';
import {
    PRODUCT_DETAILS_SECTION_ID,
    productDetailsExpandedAtom,
} from '../state/details-expanded-atom';
import SpecialtyPricingBanner from '@modules/product/component/specialty-pricing-banner/SpecialtyPricingBanner';
import TradeOnlyBanner from '@modules/product/component/trade-only-banner/TradeOnlyBanner';
import { useProductEnquiryModal } from '../hooks/use-product-enquiry-modal';
import { MobilePurchaseFlow } from './purchase/MobilePurchaseFlow';
import { DesktopPurchaseDrawer } from './purchase/DesktopPurchaseDrawer';
import { DesktopIslandEnquiryFallback } from './purchase/DesktopIslandEnquiryFallback';
import { ProductSampleLine } from './purchase/ProductShopBanner';
import {
    useSizesInUse,
    useVariantEnquiryContext,
} from './purchase/purchase-flow-parts';
import { resolveCtaState } from './purchase/cta-state';
import { useProductPrice } from './purchase/use-product-price';

/**
 * F4 / F6 — price + state-driven CTA + ecom modal.
 *
 * Right-aligned price block above the desktop CTA cluster. The primary slot
 * follows what the product can actually do: Shop Now when it is buyable, Shop
 * Sample when only a sample is, and otherwise the professional's configured
 * enquiry label (e.g. "Request Price / Brochure" — the same label the legacy
 * page shows). Visit Website + Save sit beside it.
 *
 * Commerce leading doesn't cost the page its enquiry: on those states the
 * professional's label keeps the outlined slot straight after Shop Now, so the
 * QDD action stays on the page rather than living only behind the drawer.
 *
 * Every commerce CTA opens the right slide-in drawer (`DesktopPurchaseDrawer`)
 * rather than transacting from the page — variant selector, stock/shipping and
 * a footer led by "Enquire about this configuration" with commerce (quantity +
 * add-to-cart / sample purchase) as the secondary, so the shopper configures
 * before they transact. Buyable-and-sampleable products get a quiet "Samples
 * available to order on ArchiPro" line under the cluster so the sample path
 * stays visible; every other state would just repeat its primary.
 *
 * Enquiry-led products open the drawer too whenever there is anything to pick
 * (the drawer folds the chosen variant into the enquiry), matching the meta
 * row's variant chips; with nothing to configure they skip straight to the
 * modal.
 *
 * Mobile is unchanged — it keeps Enquire as the primary with the shop banner
 * below (see `MobilePurchaseFlow`).
 *
 * Requires `VariantSelectionProvider` + `VariantOptionProvider` above it.
 */
export const ProductPurchaseSection = () => {
    const { product, primaryCTA } = useLoaderData<typeof loader>();
    const setDetailsExpanded = useSetAtom(productDetailsExpandedAtom);
    const trackCopyLink = useProductLinkTracking();
    // The statement's Read more: the description in full beside the picture,
    // as the project page's goes to its About.
    const readMoreInDetails = () => {
        const section = document.getElementById(PRODUCT_DETAILS_SECTION_ID);
        if (!section) return false;
        setDetailsExpanded(true);
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
    };
    const [open, setOpen] = useAtom(purchaseModalOpenAtom);

    const {
        priceString,
        retailPriceString,
        pricePrefix,
        isPriceRange,
        isOnSale,
        label: priceLabel,
    } = useProductPrice();
    const { selectedVariant } = useVariantSelectionState();
    const { canPurchase: sampleCanPurchase } = useSampleStock(selectedVariant);

    // Mirror the legacy Variants buyable/sample detection so the CTA reflects
    // the real commerce state rather than the professional enquiry resolver.
    const hasVariantShippingOptions =
        (selectedVariant?.ShippingProfile?.ShippingMethods ?? []).length > 0 ||
        product.Professional.PickupLocations.length > 0;
    const hasSampleShippingMethods = selectedVariant?.SampleShippingProfile
        ?.NoShipping
        ? false
        : (selectedVariant?.SampleShippingProfile?.ShippingMethods ?? [])
              .length > 0;
    const hasSampleShippingOptions =
        hasSampleShippingMethods ||
        product.Professional.PickupLocations.length > 0;

    const isMarketplace = product.Pricing.PricingType === 'ecommercePricing';
    const isBuyable =
        product.EcommerceReady &&
        isMarketplace &&
        hasVariantShippingOptions &&
        Boolean(selectedVariant?.CanPurchase);
    // A3: mirror legacy — sample purchase is independent of EcommerceReady
    // (legacy Variants.tsx:164 checks only sampleCanPurchase + hasSampleShippingOptions).
    const isSampleBuyable = sampleCanPurchase && hasSampleShippingOptions;

    // Does the product have anything to configure in the drawer?
    const { attributeMap } = useVariantOptionState();
    const sizesInUse = useSizesInUse();
    const hasVariantOptions = attributeMap.size > 0 || sizesInUse.length > 0;

    // Enquiry CTAs are gated on the professional's enquiry audience. Commerce
    // states lead with Shop Now / Shop Sample either way and reach the enquiry
    // through the drawer footer, so the gate only silences the enquiry-led
    // primary.
    const { showEnquiryCTA, showSpecifierLoginPrompt } = useEnquiryVisibility(
        product.Professional,
    );

    // A1: use the professional's configured CTA label for enquiry-led state
    // (e.g. "Request Price / Brochure") rather than generic "Enquire" /
    // "Request Quote". Falls back to the legacy defaults when absent.
    const {
        label: ctaLabel,
        isEnquiryCTA,
        opensEnquiryModalDirectly,
        showPrimaryCta,
        showSampleLine,
        showSecondaryEnquiry,
    } = resolveCtaState({
        isBuyable,
        isSampleBuyable,
        isMarketplace,
        hasVariantOptions,
        enquiryCTALabel: primaryCTA.primaryCTALabelTitleCase,
        enquiryVisible: showEnquiryCTA,
    });

    // The enquiry label always resolves to the professional's configured
    // label (or a generic fallback) — distinct from `ctaLabel`, which becomes
    // "Shop Now" on buyable products. Mobile shows Enquire on every product,
    // so it needs the enquiry label independently of the commerce state. The
    // supplier name is appended ("Get in Touch with Nero Tapware") so the CTA
    // names the counterparty — but only while the combined label still fits
    // the pill on one line. A long label ("Request Price / Brochure") or a
    // long supplier name would overflow, so those keep the bare label (the
    // trust row names the supplier right below the CTA anyway).
    //
    // The suffix earns its place where enquiry is the primary action (mobile,
    // the island, the modal header). Beside Shop Now it does not: it made the
    // outlined secondary 2.4x wider than the filled primary, so the desktop
    // CTA row uses `baseEnquiryLabel` and lets the fill carry the hierarchy.
    const baseEnquiryLabel =
        primaryCTA.primaryCTALabelTitleCase ||
        (isMarketplace ? 'Enquire' : 'Request Quote');
    const supplierName = product.Professional?.Title ?? '';
    const labelWithSupplier = `${baseEnquiryLabel} with ${supplierName}`;
    const enquiryLabel =
        supplierName &&
        !baseEnquiryLabel.includes(supplierName) &&
        labelWithSupplier.length <= 34
            ? labelWithSupplier
            : baseEnquiryLabel;

    const { openEnquiryModal } = useProductEnquiryModal();
    const onPrimaryCta = () => {
        if (opensEnquiryModalDirectly) {
            openEnquiryModal({ title: ctaLabel, ctaLabel });
            return;
        }
        setOpen(true);
    };

    // The enquiry, opened directly with the current selection folded into the
    // subject. This is what the outlined pill beside Shop Now does: the reader
    // has passed over a primary that says Shop and chosen the one that says
    // enquire, so sending them to the same drawer would make the two labels one
    // control and put a configuration step in front of a question.
    const variantEnquiry = useVariantEnquiryContext();
    const onEnquireClick = () =>
        openEnquiryModal({
            title: enquiryLabel,
            ctaLabel: enquiryLabel,
            messageSubject: variantEnquiry.messageSubject,
            itemImage: variantEnquiry.itemImage,
        });

    // The magic island's pill is the whole CTA cluster once that cluster has
    // scrolled away, not the enquiry half of it, so it goes where the primary
    // CTA goes. On a product with something to configure that is the drawer: an
    // enquiry sent from the island would otherwise carry whatever variant
    // happened to be selected, with no chance to pick, and a shop-enabled
    // product would lose its Add to Cart entirely. Only a product with nothing
    // to configure enquires straight from here.
    const onIslandClick = () => {
        if (opensEnquiryModalDirectly) {
            onEnquireClick();
            return;
        }
        setOpen(true);
    };

    // A2: "Request a Sample" enquiry — shown when the product has samples
    // available (non-buyable samples) and the enquiry CTA is active.
    const showRequestSampleEnquiry =
        Boolean(product.SamplesAvailable) && isEnquiryCTA && showEnquiryCTA;
    const onRequestSampleEnquiry = () => {
        openEnquiryModal({
            title: 'Request a Sample',
            ctaLabel: 'Request a Sample',
            // The product, not a phrase about it. The default message is
            // chosen off the CTA label and already opens "requesting a sample
            // of", so naming the subject that way too asked the supplier for a
            // sample of a sample of the product.
            messageSubject: product.Title,
        });
    };

    const { mobile } = useAppDisplayModeContext();
    if (mobile) {
        // BC-4524 — mobile runs the whole purchase path inline (no modal).
        return (
            <MobilePurchaseFlow
                priceString={priceString}
                isPriceRange={isPriceRange}
                isOnSale={isOnSale}
                retailPriceString={retailPriceString}
                pricePrefix={pricePrefix}
                isBuyable={isBuyable}
                isSampleBuyable={isSampleBuyable}
                enquiryLabel={enquiryLabel}
                showCTA={showEnquiryCTA}
                showSpecifierLoginPrompt={showSpecifierLoginPrompt}
                showRequestSampleEnquiry={showRequestSampleEnquiry}
                onRequestSampleEnquiry={onRequestSampleEnquiry}
            />
        );
    }

    // The website + save pair is identical in both places.
    const websiteAndSaveButtons = (
        <>
            <TrackedWebsiteCta
                website={product.Website || product.Professional.Website}
                libraryItemID={product.ID}
                professionalID={product.Professional.ID}
                professionalTitle={product.Professional.Title}
                variant="white-lifted"
                className="max-md:w-full"
            />
            <DetailSaveButton
                pinId={product.ID}
                pinType="ProductPin"
                hoverLabel
                className="border-transparent bg-white text-[#181818] [box-shadow:0px_4px_14px_10px_rgba(0,0,0,0.05)] hover:bg-white/70"
            />
        </>
    );

    /**
     * The primary action and its enquiry companion, shared by both copies of
     * the CTA cluster so the hero cannot drift from the summary row. Same
     * label and same handler on both, which matters beyond wording: an
     * enquiry-led product with variants opens the picker drawer first, and a
     * hero that called the enquiry modal straight would skip the selection the
     * summary row makes before enquiring.
     *
     * `showPrimaryCta` carries the audience gating — an enquiry-led product
     * whose professional has the enquiry hidden shows no primary at all rather
     * than a button that cannot open anything.
     */
    const primaryCtaPair = (
        <>
            {showPrimaryCta ? (
                <button
                    type="button"
                    onClick={onPrimaryCta}
                    className={detailCta(
                        // The one dark button on the page, and only when the
                        // product can actually be bought: a transaction is a
                        // different order of action from an enquiry, and the
                        // cluster should say which of the two this product
                        // offers before the reader reads any labels. The
                        // enquiry-led primary keeps the shared white surface.
                        isEnquiryCTA ? 'white-lifted' : 'filled-light',
                        'max-md:w-full',
                    )}
                >
                    {ctaLabel}
                </button>
            ) : null}
            {showSecondaryEnquiry ? (
                // Commerce states carry Shop Now / Shop Sample in the primary,
                // so enquiry keeps its own outlined pill beside it.
                <button
                    type="button"
                    onClick={onEnquireClick}
                    className={detailCta('white-lifted', 'max-md:w-full')}
                >
                    {baseEnquiryLabel}
                </button>
            ) : null}
        </>
    );

    /**
     * The actions cluster: the primary pair, plus the sample route where the
     * product offers one, then website and save.
     */
    const ctaButtons = (
        <>
            {primaryCtaPair}
            {showRequestSampleEnquiry ? (
                <button
                    type="button"
                    onClick={onRequestSampleEnquiry}
                    className={detailCta('white-lifted', 'max-md:w-full')}
                >
                    Request a Sample
                </button>
            ) : null}
            {websiteAndSaveButtons}
        </>
    );

    // The statement line. There is no generated-copy service on this page, so

    return (
        <>
            {/* Desktop island fallback: price + Enquire from 30% scroll depth,
                whenever no section claims the island. */}
            <DesktopIslandEnquiryFallback
                priceString={priceLabel}
                enquiryLabel={enquiryLabel}
                show={showEnquiryCTA}
                onEnquire={onIslandClick}
            />
            {/* The cluster, level with the description's first line and
                sticky from there: once the page has scrolled it up to 20px
                under the top it locks and rides the rest of the page, as the
                profile's pair does. A sibling of the summary row rather than
                its aside, because a sticky element travels only as far as
                its parent, and the aside ends with the row. The bottom
                margin gives its height back, so the row starts where it
                otherwise would; the aside keeps a blank of the same height
                in the cluster's old place, so what sits under it does not
                move up. */}
            <div className="pointer-events-none sticky top-[1.25rem] z-[120] w-full max-md:hidden md:mb-[-3.25rem] md:px-[3rem]">
                <div
                    className={cn(
                        CONTENT_CAP_PADDED,
                        'flex flex-wrap items-center justify-end gap-2 [&>*]:pointer-events-auto',
                    )}
                >
                    {ctaButtons}
                </div>
            </div>
            <DetailSummaryRow
                // The supplier's own description, leading the page, at the
                // product directory's statement size. Three lines, then Read
                // more, which goes down to the description in full beside the
                // picture.
                description={
                    // Keyed to the product for the same reason the spec list
                    // is: Read more must not stay expanded into the next one.
                    <ProductStatementText
                        key={product.ID}
                        html={product.Content ?? ''}
                        onReadMore={readMoreInDetails}
                        onLinkClick={trackCopyLink}
                    />
                }
                descriptionClassName="max-md:mt-0 md:mt-0 md:max-w-[60rem]"
                aside={
                    <>
                        {!product.IsInServiceableArea ? (
                            <p className="m-0 font-sans text-base font-normal text-[#6b6b6b]">
                                Not available in your location.
                            </p>
                        ) : null}
                        {/* The cluster's place: it is drawn by the sticky
                            sibling above, on this exact line. */}
                        <div aria-hidden="true" className="h-[3.25rem]" />
                        {/* Why the price is the price. The banner reached the
                            mobile flow and the desktop drawer but never the
                            desktop page, so a trade customer on desktop saw a
                            discounted figure with nothing saying it was their
                            discount — and a signed-out reader lost the prompt
                            that says signing in is what reveals theirs. It
                            hides itself where the product has no such price. */}
                        <SpecialtyPricingBanner product={product} />
                        {/* Its pair, and for the same reason: on a trade-only
                            variant the page otherwise shows no price and no
                            reason for its absence. Every other surface — the
                            mobile flow, the drawer, the legacy page — carries
                            the two together. */}
                        <TradeOnlyBanner selectedVariant={selectedVariant} />
                        {/* Shop Now owns the primary slot on a product that can
                            also be sampled — this quiet line keeps the sample
                            path visible, opening the same drawer. */}
                        {showSampleLine ? (
                            <ProductSampleLine onOpen={() => setOpen(true)} />
                        ) : null}
                        {isEnquiryCTA &&
                        !showEnquiryCTA &&
                        showSpecifierLoginPrompt ? (
                            <SpecifierLoginPrompt
                                professionalName={product.Professional.Title}
                                showBottomBorder={false}
                                className="mt-4"
                            />
                        ) : null}
                    </>
                }
            />

            <DesktopPurchaseDrawer
                open={open}
                onOpenChange={setOpen}
                isBuyable={isBuyable}
                isSampleBuyable={isSampleBuyable}
                priceString={priceString}
                isPriceRange={isPriceRange}
                isOnSale={isOnSale}
                retailPriceString={retailPriceString}
                pricePrefix={pricePrefix}
                enquiryLabel={enquiryLabel}
                showCTA={showEnquiryCTA}
                showSpecifierLoginPrompt={showSpecifierLoginPrompt}
            />
        </>
    );
};
